import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, Clock } from "lucide-react";
import { CONTACT } from "./constants";

export default function Contact() {
  return (
    <section
      id="iletisim"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 bg-white overflow-hidden"
    >
      <div
        className="absolute inset-0 ab-grid-bg opacity-40"
        aria-hidden
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-3xl mx-auto">
          <span className="ab-chip">İletişim</span>
          <h2 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[var(--ab-navy)] leading-[1.05]">
            Bir kahve içelim,
            <br />
            <span className="ab-text-gradient">projenizi konuşalım</span>.
          </h2>
          <p className="mt-5 text-lg text-slate-600">
            Dijital projenizi konuşmak için hemen arayın veya WhatsApp&apos;tan yazın.
            Aynı gün dönüş garantisi.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-6">
          {/* Big CTA card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 rounded-3xl bg-[var(--ab-navy)] text-white p-8 sm:p-12 relative overflow-hidden"
          >
            <div
              className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-40"
              style={{
                background:
                  "radial-gradient(circle at center, #2D3FFF 0%, transparent 70%)",
              }}
              aria-hidden
            />
            <div className="relative">
              <div className="text-xs font-semibold uppercase tracking-widest text-blue-200">
                Hızlı İletişim
              </div>
              <h3 className="font-display mt-3 text-3xl sm:text-4xl font-extrabold leading-tight">
                Tek tıkla ulaşın,
                <br />
                aynı gün cevap alın.
              </h3>
              <p className="mt-4 text-blue-100 max-w-md">
                Form doldurmaya gerek yok. WhatsApp veya telefon hattımız her zaman açık.
              
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={CONTACT.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="ab-btn-wa inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-semibold"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp&apos;tan Yaz
                </a>
                <a
                  href={CONTACT.phoneLink}
                  data-testid="contact-phone-btn"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-semibold bg-white text-[var(--ab-navy)] hover:bg-blue-50 transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  {CONTACT.phoneDisplay}
                </a>
              </div>

              <div className="mt-10 pt-8 border-t border-white/10 grid sm:grid-cols-2 gap-5">
                <a
                  href={CONTACT.emailLink}
                  data-testid="contact-email-link"
                  className="flex items-start gap-3 group"
                >
                  <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0 group-hover:bg-[var(--ab-blue)] transition-colors">
                    <Mail className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-blue-200">
                      E-posta
                    </div>
                    <div className="font-semibold">{CONTACT.email}</div>
                  </div>
                </a>
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-blue-200">
                      Adres
                    </div>
                    <div className="font-semibold">{CONTACT.address}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                    <Clock className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-blue-200">
                      Çalışma Saatleri
                    </div>
                    <div className="font-semibold">Pzt - Cmt · 09:00 - 19:00</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                    <Phone className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-blue-200">
                      Telefon
                    </div>
                    <div className="font-semibold">{CONTACT.phoneDisplay}</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Map / Address card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5 rounded-3xl border border-slate-200 bg-white overflow-hidden flex flex-col"
            data-testid="contact-map-card"
          >
            <div className="aspect-[4/3] w-full relative bg-slate-100">
              <iframe
                title="AjansBilir Konum"
                src="https://www.google.com/maps?q=Beylikd%C3%BCz%C3%BC%2C%20%C4%B0stanbul&output=embed"
                className="absolute inset-0 w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="p-6 sm:p-8">
              <div className="text-xs font-semibold uppercase tracking-widest text-[var(--ab-blue)]">
                Ofisimiz
              </div>
              <h3 className="font-display mt-2 text-2xl font-bold text-[var(--ab-navy)]">
                Beylikdüzü, İstanbul
              </h3>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                Modern, açık ofisimizde sizi her zaman ağırlamaktan mutluluk
                duyarız. Görüşmeden önce randevu almanızı rica ederiz.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
